import * as React from 'react';
import type {
  Automation,
  AutomationActionTriggerData, AUTOMATION_ACTION_TRIGGER_TYPE
} from '../../../../MedicareProAPI/Entities/Automation/Automation';
import {
  AUTOMATION_TRIGGER_COMBINATION
} from '../../../../MedicareProAPI/Entities/Automation/Automation';
import type { AutomationAction } from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AUTOMATION_ACTION_TYPE } from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import { Utilities } from '../../../../MedicareProAPI/Utilities/Utilities';
import type { INTEGRATION_TYPE } from '../../../Constants/Integrations';
import {
  AUTOMATION_ACTION_TYPE_LABELS
} from '../../../Language/en';
import StyledModal from '../../StyledModal';
import { AutomationActionFormAction } from '../AutomationActionFormAction';
import { AutomationActionFormTrigger } from '../AutomationActionFormTrigger';

export interface AutomationActionFormProps {
  automation: Automation;
  action: AutomationAction;
  userAccessToken: string;
  userId: number;
  shared: boolean;
  expanded?: boolean;
  automationHasParent: boolean;
  actionHasParent: boolean;
  onSave: (action: AutomationAction) => Promise<void | AutomationAction>;
  onDelete: (action: AutomationAction) => Promise<void | AutomationAction>;
  onSetupIntegrationService?: (type: INTEGRATION_TYPE) => void;
  onSetupTemplate?: () => void;
  refreshKey?: number;
}

export const AutomationActionForm = (props: AutomationActionFormProps): React.JSX.Element => {
  const canUseFlows: number[] = [1, 4169, 3459, 7194, 670, 5172, 4298, 7332];
  const [action, setAction] = React.useState(null);

  const [automationHasParent, setAutomationHasParent] = React.useState<boolean>(
    props.automationHasParent
  );
  const [actionHasParent, setActionHasParent] = React.useState<boolean>(props.actionHasParent);
  const [formTriggerType, setFormTriggerType] =
    React.useState<AUTOMATION_ACTION_TRIGGER_TYPE>(null);
  const [formTriggerCriteria, setFormTriggerCriteria] =
    React.useState<AutomationActionTriggerData[]>();
  const [formRecipe, setFormRecipe] = React.useState<AUTOMATION_TRIGGER_COMBINATION>(null);
  const [formType, setFormType] = React.useState<AUTOMATION_ACTION_TYPE>(null);
  const [formId, setFormId] = React.useState<number>(null);

  const [formData, setFormData] = React.useState({});
  const [formStartDelayTime, setStartDelayTime] = React.useState(null);
  const [formStartDelayDays, setFormStartDelayDays] = React.useState(null);
  const [deleteModal, setDeleteModal] = React.useState(false);

  const [formLoading, setFormLoading] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  /*const delayDaysOptions = [
        {value: '', label: "Immediately"},
        {value: 0, label: "Same Day"},
        {value: 1, label: "1 Day"},
        {value: 2, label: "2 Days"},
        {value: 3, label: "3 Days"},
        {value: 4, label: "4 Days"},
        {value: 5, label: "5 Days"},
        {value: 6, label: "6 Days"},
        {value: 7, label: "1 Week"},
        {value: 14, label: "2 Weeks"},
        {value: 21, label: "3 Weeks"},
        {value: 30, label: "1 Month"},
        {value: 60, label: "2 Months"},
        {value: 90, label: "3 Months"},
        {value: 180, label: "6 Months"},
    ];*/

  const delayDaysOptions = [
    { value: '', label: 'Immediately' },
    { value: 0, label: 'Same Day' },
    { value: 1, label: '1 Day' },
    { value: 2, label: '2 Days' },
    { value: 3, label: '3 Days' },
    { value: 4, label: '4 Days' },
    { value: 5, label: '5 Days' },
    { value: 6, label: '6 Days' },
    { value: 7, label: '1 Week' },
    { value: 14, label: '2 Weeks' },
    { value: 21, label: '3 Weeks' },
    { value: 30, label: '1 Month' },
    { value: 60, label: '2 Months' },
    { value: 90, label: '3 Months' },
    { value: 120, label: '4 Months' },
    { value: 150, label: '5 Months' },
    { value: 180, label: '6 Months' },
    { value: 210, label: '7 Months' },
    { value: 240, label: '8 Months' },
    { value: 270, label: '9 Months' },
    { value: 300, label: '10 Months' },
    { value: 330, label: '11 Months' },
    { value: 360, label: '1 Year' },
  ];

  React.useEffect(() => {
    handleActionChange(props.action);
  }, [props.action]);

  const saveAutomation = () => {
    setFormLoading(true);

    action
      .setType(formType)
      .setData(formData)
      .setTriggerCriteria(formTriggerCriteria)
      .setStartDelayTime(formStartDelayTime)
      .setStartDelayDays(formStartDelayDays);

    if (props.onSave) {
      props.onSave(action).then((updateAction: AutomationAction) => {
        setFormLoading(false);
        handleActionChange(updateAction);
      });
    } else {
      setFormLoading(false);
    }
  };

  const handleActionChange = (action: AutomationAction) => {
    if (action) {
      setAction(action);
      setFormId(action.getId());
      setFormType(action.getType());
      setFormData(action.getData() || {});
      setFormTriggerCriteria(action.getTriggerCriteria());
      setStartDelayTime(action.getStartDelayTime());
      setFormStartDelayDays(action.getStartDelayDays());

      // This data is not present is automation if being shared so we have to get it from the parent?
      const parentAutomation = props.automation.getParentAutomation();

      if (parentAutomation) {
        setFormTriggerType(parentAutomation.getTriggerType());
        setFormRecipe(parentAutomation.getRecipe());
      } else {
        setFormTriggerType(props.automation.getTriggerType());
        setFormRecipe(props.automation.getRecipe());
      }
    } else {
      setAction(null);
      setFormId(null);
      setFormType(null);
      setFormData(null);
      setFormTriggerCriteria(null);
      setStartDelayTime(null);
      setFormStartDelayDays(null);
      setFormTriggerType(null);
      setFormRecipe(null);
    }
  };

  const handleActionTypeChange = (event) => {
    const newFormType = event.target.value as AUTOMATION_ACTION_TYPE;
    setFormType(newFormType);
    setFormData({});
  };

  const startTimeClass = ['badge', 'badge-pill', 'badge-success', 'p-2', 'px-3'];
  const formStartDelayDaysLabel = delayDaysOptions.find((item) =>
    formStartDelayDays == null ? '' === item.value : formStartDelayDays === item.value
  ).label;

  if (formStartDelayDaysLabel == 'Immediately') {
    startTimeClass.push('bg-success');
  } else if (formStartDelayDaysLabel == 'Same Day') {
    startTimeClass.push('bg-teal');
  } else if (formStartDelayDaysLabel.indexOf('Day') >= 0) {
    startTimeClass.push('bg-cyan');
  } else if (formStartDelayDaysLabel.indexOf('Week') >= 0) {
    startTimeClass.push('bg-indigo');
  } else if (formStartDelayDaysLabel.indexOf('Month') >= 0) {
    startTimeClass.push('bg-salmon');
  }

  const deleteAutomation = () => {
    setDeleting(true);

    if (props.onDelete) {
      props.onDelete(action).then((response) => {
        setDeleting(false);
        setDeleteModal(false);
      });
    }
  };

  return (
    <>
      <StyledModal
        size="md"
        title="Delete Action"
        footerCloseButton={true}
        footerButtons={
          <button onClick={(e) => deleteAutomation()} className="btn btn-danger" type="button">
            {deleting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Deleting
              </>
            ) : (
              <>Delete</>
            )}
          </button>
        }
        show={deleteModal}
        onClose={(e) => setDeleteModal(false)}
      >
        <div className="row pl-3 pr-3">
          <p>Are you sure you want to delete this action? This can't be undone.</p>
        </div>
      </StyledModal>

      {formTriggerType && (
        <div className="row mt-5">
          <div className="col-6 mb-3">
            <h6>
              <span className=" font-weight-bold calMess">When should this action trigger?</span>
            </h6>
            {automationHasParent && (
              <span className="alert alert-warning p-1 mt-1" style={{ fontSize: '9pt' }}>
                <i className="far fa-exclamation mr-1 "></i>
                These values were set up by the shared automation creator and cannot be edited.
              </span>
            )}
          </div>
          <div className="mb-1 col-6"></div>
          <div className="mb-1 col-6">
            <label className="label">Trigger Delay</label>
            <div className="input-group input-group-sm">
              <select
                disabled={formLoading || props.shared || props.automationHasParent}
                className="form-control form-control-sm"
                onChange={(e) =>
                  setFormStartDelayDays(e.target.value === '' ? null : parseInt(e.target.value))
                }
              >
                {delayDaysOptions.map((item) => (
                  <option selected={formStartDelayDays === item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <small>
              <i className="far fa-question-circle mr-1 "></i>
              {actionHasParent ? (
                <>Amount of time after the parent event is triggered.</>
              ) : (
                <>
                  Amount of time after the initial event occurs.
                  {formRecipe === AUTOMATION_TRIGGER_COMBINATION.IND_STATUS_INSERT &&
                    !formStartDelayDays && (
                      <>
                        {' '}
                        Immediate actions will have a 15 minute delay to allow the addition of data
                        to new profile.
                      </>
                    )}
                </>
              )}
            </small>
          </div>
          {formStartDelayDays !== null && (
            <div className="mb-1 col-6">
              <label className="label">Trigger Time</label>
              <div className="input-group input-group-sm">
                <input
                  disabled={formLoading || props.shared || props.automationHasParent}
                  type="time"
                  className="form-control border-secondary"
                  autoComplete="off"
                  value={
                    formStartDelayTime ? Utilities.dateTimeToTimeString(formStartDelayTime) : null
                  }
                  onChange={(e) =>
                    setStartDelayTime(Utilities.timeStringToDateTime(e.target.value))
                  }
                />
              </div>
              <small>
                <i className="far fa-question-circle mr-1"></i>What time of day should this action
                trigger?{' '}
                {formStartDelayDays === 0 && (
                  <>
                    You've selected this action to trigger on the same day. If the action occurs
                    after this time, the automation will be executed immediately.
                  </>
                )}
              </small>
            </div>
          )}
        </div>
      )}
      {formRecipe && props.shared === false && (
        <>
          <div className="row">
            <div className="col-12 mb-3 mt-5">
              <h6>
                <span className=" font-weight-bold calMess">What should this action do?</span>
              </h6>
              {Object.keys(formData).length === 0 && automationHasParent && (
                <span className="alert alert-warning p-1 mt-1" style={{ fontSize: '9pt' }}>
                  <i className="far fa-exclamation mr-1 "></i>
                  Because this is a shared automaton, the services originally selected to perform
                  this action may not be available to you. You will need to set up a service below.
                </span>
              )}
            </div>
            <div className="mb-3 col-4">
              <label className="label d-block">Action Type</label>
              <div className="input-group input-group-sm">
                <select
                  className="form-control form-control-sm"
                  value={formType}
                  onChange={(e) => handleActionTypeChange(e)}
                >
                  <option></option>
                  {AUTOMATION_ACTION_TYPE_LABELS.map((item) => {
                    if (
                      item.value === AUTOMATION_ACTION_TYPE.FLOW_UPSERT &&
                      !canUseFlows.includes(Number(props.userId))
                    ) {
                      return null;
                    }

                    return (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>

          {formType && (
            <AutomationActionFormAction
              userId={props.userId}
              userAccessToken={props.userAccessToken}
              actionData={formData}
              shared={props.shared}
              automationHasParent={automationHasParent}
              type={formType}
              onChange={(newFormData) => {
                setFormData(newFormData);
              }}
              onSetupIntegrationService={props.onSetupIntegrationService}
              onSetupTemplate={props.onSetupTemplate}
              refreshKey={props.refreshKey}
            />
          )}

          <div className="row mt-5">
            <div className="mb-1 col-12">
              <h6>
                <span className=" font-weight-bold calMess">
                  Only trigger this action if the following is true
                </span>
              </h6>

              <AutomationActionFormTrigger
                key={props.automation?.getId()}
                automationId={props.automation?.getId()}
                userId={props.userId}
                userAccessToken={props.userAccessToken}
                recipe={formRecipe}
                triggerData={formTriggerCriteria}
                shared={props.shared}
                automationHasParent={props.automationHasParent}
                onChange={(newFormData) => {
                  setFormTriggerCriteria(newFormData);
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-sm-6">
              <button
                disabled={props.shared}
                type="button"
                onClick={(e) => saveAutomation()}
                className="btn btn-primary"
              >
                {formLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> {formId ? 'Saving' : 'Creating'}
                  </>
                ) : (
                  <>{action && action.getId() ? 'Save' : 'Create'}</>
                )}
              </button>
            </div>
            <div className="col-sm-6">
              {formId && (!props.shared || !props.automationHasParent) && (
                <span
                  onClick={(e) => setDeleteModal(true)}
                  className="align-text-bottom cp text-danger float-right"
                >
                  {deleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Deleting
                    </>
                  ) : (
                    <i className="far fa-trash-alt"></i>
                  )}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
